import React, { useEffect, useState } from "react";
import SidebarMember from "../../components/Sidebar/SidebarMember"; // ✅ Import Sidebar
import { db, auth } from "../../firebase";
import { collection, onSnapshot, addDoc, query, where, serverTimestamp, doc, updateDoc, increment, getDoc } from "firebase/firestore";
import "./MemberDashboard.css";

export default function MemberDashboard() {
  const [groups, setGroups] = useState([]);
  const [requests, setRequests] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const user = auth.currentUser;
  const unreadCount = notifications.filter(n => !n.read).length;
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [userName, setUserName] = useState("");
  const [activeTab, setActiveTab] = useState('available'); // 'available' or 'my-groups'
  const [showAllGroups, setShowAllGroups] = useState(false);
  useEffect(() => {
  if (!auth.currentUser) return;

  const user = auth.currentUser;

  if (user.displayName) {
    setUserName(user.displayName);
  } else if (user.email) {
    setUserName(user.email.split("@")[0]);
  }
}, []);


  useEffect(() => {
    // Real-time listener for groups - dynamically computes availability
    const unsub = onSnapshot(collection(db, "groups"), (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Filter groups that have available slots
      // Handle cases where currentMembers or maxMembers might be undefined/null
      const available = data.filter((g) => {
        const currentMembers = g.currentMembers || 0;
        const maxMembers = g.maxMembers || 0;
        // Show group if it has available slots (currentMembers < maxMembers)
        return currentMembers < maxMembers;
      });
      
      setGroups(available);
    });

    return () => unsub();
  }, []);

  // Fetch join requests by this user
  useEffect(() => {
    if (!user?.uid) return;
    const q = query(collection(db, "joinRequests"), where("memberId", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      setRequests(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Fetch notifications for this user
  useEffect(() => {
    if (!user?.uid) {
      console.log("❌ Dashboard Notifications: User UID not available");
      return;
    }
    console.log("🔍 Dashboard: Fetching ALL notifications for user:", user.uid);
    const q = query(collection(db, "notifications"), where("to", "==", user.uid));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      data.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      console.log("✅ Dashboard Notifications fetched:", data.length, "notifications");
      setNotifications(data);

      // Do NOT auto-open notifications on new items — user must click the bell to view
    });
    return () => unsub();
  }, [user, showNotifications]);

  // Open group details modal — try to use cached groups, otherwise fetch from Firestore
  const openGroupDetails = async (groupId, fallbackName, request) => {
    try {
      let g = groups.find(x => x.id === groupId);
      if (!g) {
        const gRef = doc(db, 'groups', groupId);
        const snap = await getDoc(gRef);
        if (snap.exists()) g = { id: snap.id, ...snap.data() };
      }
      if (!g) g = { id: groupId, groupName: fallbackName || 'Group' };
      setSelectedGroup({ ...g, request });
      setShowGroupModal(true);
    } catch (err) {
      console.error('Error opening group details:', err);
      alert('Could not load group details');
    }
  };

  const leaveGroup = async () => {
    if (!window.confirm('Are you sure you want to leave this group?')) return;
    if (!selectedGroup?.request?.id) {
      alert('Cannot leave group: request not found');
      return;
    }
    try {
      await updateDoc(doc(db, 'joinRequests', selectedGroup.request.id), { status: 'left' });
      setShowGroupModal(false);
      alert('Left group successfully');
    } catch (err) {
      console.error('Error leaving group:', err);
      alert('Could not leave group');
    }
  };

  const messageOrganizer = () => {
    if (!selectedGroup?.organizerId) {
      alert('Organizer info not available');
      return;
    }
    alert(`Message feature for ${selectedGroup.organizerName || selectedGroup.organizerId} (coming soon)`);
  };

  // Mark a single notification as read (keeps it in the list)
  const markAsRead = async (notifId) => {
    try {
      const notifRef = doc(db, "notifications", notifId);
      await updateDoc(notifRef, { read: true });
      console.log("Marked notification read:", notifId);
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const requestToJoin = async (group) => {
    if (!user) {
      alert("You must be logged in");
      return;
    }
    try {
      console.log("📝 Creating join request for group:", group.groupName, "Member UID:", user.uid);
      console.log("📋 Group data:", group);
      
      // Get user profile to retrieve phone number
      let memberPhone = "";
      try {
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        if (userDocSnap.exists()) {
          memberPhone = userDocSnap.data().phone || userDocSnap.data().phoneNumber || "";
        }
      } catch (err) {
        console.warn("Could not fetch user phone number:", err);
      }

      // If phone number is missing, prompt user to add it
      if (!memberPhone || memberPhone === "" || memberPhone === "0000000000") {
        const phoneInput = prompt("📱 Please enter your 10-digit phone number to continue:\n\nThis will be shared with the organizer.", "");
        
        if (!phoneInput) {
          alert("Phone number is required to send a join request.");
          return;
        }

        // Validate phone number (10 digits)
        const phoneRegex = /^[0-9]{10}$/;
        if (!phoneRegex.test(phoneInput)) {
          alert("❌ Invalid phone number. Please enter exactly 10 digits.");
          return;
        }

        memberPhone = phoneInput;

        // Update user profile with phone number
        try {
          const userDocRef = doc(db, "users", user.uid);
          await updateDoc(userDocRef, {
            phone: memberPhone,
            phoneNumber: memberPhone, // Store in both fields for compatibility
          });
          console.log("✅ Phone number saved to user profile");
        } catch (err) {
          console.error("Error saving phone number:", err);
          // Continue anyway - we have the phone number for this request
        }
      }
      
      // optimistic UI: add a local request with status 'processing'
      const tempReq = {
        id: `temp-${Date.now()}`,
        groupId: group.id,
        groupName: group.groupName,
        memberId: user.uid,
        memberName: user.displayName || "Unknown",
        memberEmail: user.email,
        memberPhone: memberPhone,
        status: "processing",
        createdAt: serverTimestamp(),
      };
      setRequests((prev) => [tempReq, ...prev]);

      // create join request in Firestore with status 'processing'
      const docRef = await addDoc(collection(db, "joinRequests"), {
        groupId: group.id,
        groupName: group.groupName,
        memberId: user.uid,
        memberName: user.displayName || "Unknown",
        memberEmail: user.email,
        memberPhone: memberPhone,
        status: "processing",
        createdAt: serverTimestamp(),
      });
      console.log("✅ Join request created with ID:", docRef.id);
      console.log("📝 Document created in collection: joinRequests");

      // notify organizer (ONLY if organizerId exists)
      if (group.organizerId) {
        console.log("📧 Found organizerId:", group.organizerId, "- Creating notification");
        await addDoc(collection(db, "notifications"), {
          to: group.organizerId,
          type: "join_request",
          message: `${user.displayName || 'A member'} requested to join ${group.groupName}`,
          groupId: group.id,
          memberId: user.uid,
          createdAt: serverTimestamp(),
          read: false,
        });
        console.log("✅ Organizer notification created");
      } else {
        console.warn("⚠️ WARNING: organizerId NOT FOUND on group:", group.id, "- Organizer won't be notified");
      }
      
      alert(`Request sent to join ${group.groupName}`);
    } catch (err) {
      console.error("❌ Error sending join request:", err);
      alert("Error sending request. Check console.");
    }
  };

  // Make a payment (copied simplified from MyGroups)
  const makePayment = async (group) => {
    if (!user) return alert("Login required");
    
    if (!group.contribution || group.contribution <= 0) {
      alert("Invalid contribution amount");
      return;
    }

    const options = {
      key: "rzp_test_1DP5mmOlF5G1ag", // Replace with your Razorpay Key ID
      amount: group.contribution * 100, // Amount in paise (₹ to paise)
      currency: "INR",
      name: group.groupName,
      description: `Payment for ${group.groupName}`,
      prefill: {
        name: user.displayName || "Member",
        email: user.email || "",
      },
      handler: async function (response) {
        try {
          // Payment successful - record in database
          await addDoc(collection(db, "payments"), {
            groupId: group.id,
            groupName: group.groupName,
            memberId: user.uid,
            memberName: user.displayName || "Member",
            amount: group.contribution,
            status: "completed",
            razorpayPaymentId: response.razorpay_payment_id,
            razorpayOrderId: response.razorpay_order_id || null,
            razorpaySignature: response.razorpay_signature || null,
            createdAt: serverTimestamp(),
          });

          // update joinRequest (mark lastPaidAt)
          const req = requests.find((r) => r.groupId === group.id && r.memberId === user.uid && r.status === 'accepted');
          if (req) {
            const reqRef = doc(db, "joinRequests", req.id);
            await updateDoc(reqRef, { lastPaidAt: serverTimestamp(), paid: true });
          }

          // update group totals
          const groupRef = doc(db, "groups", group.id);
          await updateDoc(groupRef, {
            totalCollected: increment(group.contribution || 0),
            membersPaid: increment(1),
          });

          // notify organizer
          if (group.organizerId) {
            await addDoc(collection(db, "notifications"), {
              to: group.organizerId,
              type: "payment",
              message: `${user.displayName || 'Member'} paid ₹${group.contribution} for ${group.groupName}`,
              groupId: group.id,
              memberId: user.uid,
              createdAt: serverTimestamp(),
              read: false,
            });
          }

          alert(`Payment Successful!\n\nAmount: ₹${group.contribution}\nGroup: ${group.groupName}\nTransaction ID: ${response.razorpay_payment_id}`);
        } catch (err) {
          console.error("Error recording payment:", err);
          alert("Payment completed but failed to record. Contact support with Transaction ID: " + response.razorpay_payment_id);
        }
      },
      modal: {
        ondismiss: function () {
          alert("Payment cancelled. Please try again.");
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const acceptedRequests = requests.filter(r => r.status === 'accepted');
  const displayedGroups = showAllGroups ? groups : groups.slice(0, 6);
  const displayedAccepted = showAllGroups ? acceptedRequests : acceptedRequests.slice(0, 6);

  return (
    <div className="dashboard-layout">
      <SidebarMember /> {/* ✅ Sidebar is here */}
      <div className="member-dashboard">
        <div className="welcome-banner">
          <h1>Welcome, {userName} 👋</h1>

          <p>Browse groups, send join requests, and track your requests.</p>
        </div>

        {/* inline preview removed — notifications are now accessed via the top-right bell (dropdown shows history) */}

        <div className="dashboard-header">
          <div className="header-actions">
            <div className="notifications-bell" role="button" tabIndex={0} onClick={() => setShowNotifications(s => !s)} onKeyPress={(e)=>{ if(e.key==='Enter') setShowNotifications(s=>!s)}}>
              <span className="bell">🔔</span>
                {unreadCount > 0 && (
                  <span className="badge">{unreadCount}</span>
                )}
            </div>
          </div>
        </div>

        {showNotifications && (
          <div className="notifications-panel dropdown">
            <h3>Recent Notifications</h3>
            {notifications.length === 0 && <div className="notification-item">No notifications</div>}
            {notifications.map((n) => (
              <div key={n.id} className="notification-item" onClick={() => markAsRead(n.id)} style={{ background: n.read ? '#f9f9f9' : '#e8f5e9', borderLeft: n.read ? '4px solid #ccc' : '4px solid #28a745', cursor: 'pointer' }}>
                <div style={{ fontWeight: 700, color: n.read ? '#666' : '#155724' }}>{n.message}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: '4px' }}>{n.createdAt?.toDate ? new Date(n.createdAt.seconds * 1000).toLocaleString() : 'Just now'}</div>
              </div>
            ))}
          </div>
        )}

        <div className="tabs-container">
          <button 
            className={`tab-button ${activeTab === 'available' ? 'active' : ''}`}
            onClick={() => setActiveTab('available')}
          >
            Available Groups ({groups.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'my-groups' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-groups')}
          >
            My Groups ({acceptedRequests.length})
          </button>
        </div>

        {activeTab === 'available' && (
          <div className="group-container">
            {groups.length === 0 && <p className="empty-state">No groups available now.</p>}
            {displayedGroups.map((g) => {
              const myReq = requests.find(r => r.groupId === g.id && r.memberId === user?.uid);
              // If member was removed, treat as if no request exists (show as available)
              const shouldShowAsAvailable = !myReq || myReq.status === 'removed' || myReq.status === 'rejected' || myReq.status === 'left';
              // Safely calculate with fallback values
              const currentMembers = g.currentMembers || 0;
              const maxMembers = g.maxMembers || 0;
              return (
                <div key={g.id} className="group-card">
                  <h3>{g.groupName}</h3>
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                      <strong style={{ color: '#111827' }}>Monthly Contribution:</strong> ₹{g.contribution || 0}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                      <strong style={{ color: '#111827' }}>Total Money:</strong> ₹{g.totalMoney || (maxMembers * (g.contribution || 0))}
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                      <strong style={{ color: '#111827' }}>Duration:</strong> {g.months || 'N/A'} months
                    </p>
                    <p style={{ margin: '4px 0', fontSize: '14px', color: '#6b7280' }}>
                      <strong style={{ color: '#111827' }}>Commission:</strong> ₹{g.commission || 0}/month
                    </p>
                  </div>
                  <p>
                    Members: {currentMembers} / {maxMembers}
                  </p>
                  {myReq && !shouldShowAsAvailable ? (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className={`status-badge ${myReq.status === 'processing' ? 'status-pending' : myReq.status === 'pending' ? 'status-pending' : myReq.status === 'accepted' ? 'status-accepted' : 'status-rejected'}`}>
                        {myReq.status === 'processing' ? 'Processing' : myReq.status === 'pending' ? 'Pending' : myReq.status === 'accepted' ? 'Accepted' : 'Rejected'}
                      </span>
                      {myReq.status === 'accepted' ? (
                        <button className="join-btn" onClick={() => makePayment(g)} style={{ flex: 1 }}>Pay ₹{g.contribution || '—'}</button>
                      ) : null}
                    </div>
                  ) : (
                    <button className="join-btn" onClick={() => requestToJoin(g)}>
                      Request to Join
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {activeTab === 'my-groups' && (
          <div className="group-container">
            {acceptedRequests.length === 0 ? (
              <p className="empty-state">No active groups. Browse available groups to join!</p>
            ) : (
              displayedAccepted.map(req => {
                const group = groups.find(g => g.id === req.groupId);
                if (!group) return null;
                const currentMembers = group.currentMembers || 0;
                const maxMembers = group.maxMembers || 0;
                return (
                  <div key={req.id} className="group-card">
                    <h3>{req.groupName}</h3>
                    <div className="group-info-compact">
                      <span>₹{group.contribution || 0}/mo</span>
                      <span>•</span>
                      <span>{currentMembers}/{maxMembers} members</span>
                      <span>•</span>
                      <span>{group.months || 'N/A'} months</span>
                    </div>
                    <div className="card-actions">
                      <button className="join-btn" onClick={() => makePayment(group)}>Pay ₹{group.contribution || '—'}</button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {(activeTab === 'available' && groups.length > 6) || (activeTab === 'my-groups' && acceptedRequests.length > 6) ? (
          <div className="show-more-container">
            <button className="show-more-btn" onClick={() => setShowAllGroups(!showAllGroups)}>
              {showAllGroups ? 'Show Less' : `Show All (${activeTab === 'available' ? groups.length - 6 : acceptedRequests.length - 6} more)`}
            </button>
          </div>
        ) : null}

        {/* Group details modal */}
        {showGroupModal && selectedGroup && (
          <div className="modal-backdrop" onClick={() => setShowGroupModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{selectedGroup.groupName}</h3>
                <button className="close-btn" onClick={() => setShowGroupModal(false)}>✕</button>
              </div>
              <div className="modal-body">
                <p><strong>Contribution:</strong> ₹{selectedGroup.contribution || '—'}</p>
                <p><strong>Members:</strong> {selectedGroup.currentMembers || '—'} / {selectedGroup.maxMembers || '—'}</p>
                {selectedGroup.description && <p><strong>About:</strong> {selectedGroup.description}</p>}
                <p><strong>Organizer:</strong> {selectedGroup.organizerName || selectedGroup.organizerId || '—'}</p>
                <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
                  <button className="join-btn" onClick={() => messageOrganizer()} style={{ flex: 1 }}>Message Organizer</button>
                  {selectedGroup.request?.status === 'accepted' && (
                    <button className="join-btn" onClick={() => leaveGroup()} style={{ background: '#dc2626', flex: 1 }}>Leave Group</button>
                  )}
                  <button className="join-btn" onClick={() => window.location.href = `/my-groups/${selectedGroup.id}`} style={{ background: '#6b7280', flex: 1 }}>Details</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Your Requests */}
        
      </div>
    </div>
  );
} 